/**
 * DefaultValueField - author-set fallback for a field (CWL-470).
 *
 * Pre-filled on mobile only when SAP auto-populate supplies nothing. Choice types
 * pick from their own options and a checkbox ticks any of them; anything else is free
 * text checked by the shared contract rule, so what the builder accepts is exactly what
 * the server applies. The inline error also shows the mobile-app rules the builder
 * enforces on edited defaults (UK mobile tel, Min/Max, tick counts).
 */
import { cn } from '@/shared/lib/cn';
import { Button, Checkbox, Input, Label } from '@/shared/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui';
import {
  DefaultValueControlType,
  checkDefaultValue,
  supportsDefaultValue,
} from '@skewb-works/contracts';
import { getDefaultValueError, getDeviceDefaultError } from './builder-utils';

export const AUTO_POPULATE_DEFAULT_HELP =
  'Used only when no value is received from SAP for this job.';

// Radix Select disallows an empty-string item value (react-select index.mjs:823).
const NO_DEFAULT = '__no_default__';

interface DefaultValueFieldControl {
  control_type: string;
  default_value?: string | string[];
  /** Form JSON is untyped legacy data: entries and their text may be missing (CWL-470). */
  options?: ReadonlyArray<{ option?: unknown } | null | undefined> | null;
  conditions?: ReadonlyArray<{ condition_name?: unknown } | null | undefined> | null;
  min?: string;
  max?: string;
  hidden?: boolean;
  read_only?: boolean;
}

interface DefaultValueFieldProps {
  id: string;
  control: DefaultValueFieldControl;
  autoPopulated: boolean;
  compact?: boolean;
  onChange: (defaultValue: string | string[]) => void;
}

const isChoiceText = (text: unknown): text is string =>
  typeof text === 'string' && text.trim() !== '';

/** Unique non-blank choices, or null when the type takes free text. */
function choicesFor(control: DefaultValueFieldControl): string[] | null {
  if (
    control.control_type === DefaultValueControlType.RADIO ||
    control.control_type === DefaultValueControlType.OPTION
  ) {
    return [...new Set((control.options ?? []).map((o) => o?.option).filter(isChoiceText))];
  }
  if (control.control_type === DefaultValueControlType.CONDITIONAL_FIELD) {
    return [
      ...new Set((control.conditions ?? []).map((c) => c?.condition_name).filter(isChoiceText)),
    ];
  }
  return null;
}

const checkboxChoicesFor = (control: DefaultValueFieldControl): string[] => [
  ...new Set((control.options ?? []).map((o) => o?.option).filter(isChoiceText)),
];

/** Stored ticks resolved to option text exactly as the server resolves them; unmatched ones are stale. */
function resolveTicks(control: DefaultValueFieldControl): { ticked: string[]; stale: boolean } {
  const raw = control.default_value;
  const listed: unknown[] = Array.isArray(raw) ? raw : [raw];
  const ticked: string[] = [];
  let stale = false;
  for (const entry of listed.filter(isChoiceText)) {
    const check = checkDefaultValue({
      control_type: DefaultValueControlType.OPTION,
      options: control.options,
      default_value: entry,
    });
    if (check.kind === 'valid') ticked.push(check.value);
    else stale = true;
  }
  return { ticked, stale };
}

/** The tick list after a toggle, in option order; stale ticks are dropped. */
const toggleTick = (choices: string[], ticked: string[], choice: string, on: boolean): string[] =>
  choices.filter((c) => (c === choice ? on : ticked.includes(c)));

const hasStoredDefault = (raw: unknown): boolean =>
  Array.isArray(raw) ? raw.some(isChoiceText) : isChoiceText(raw);

export default function DefaultValueField({
  id,
  control,
  autoPopulated,
  compact = false,
  onChange,
}: DefaultValueFieldProps) {
  const current = typeof control.default_value === 'string' ? control.default_value : '';
  const supported = supportsDefaultValue(control.control_type);
  if (!supported && !hasStoredDefault(control.default_value)) return null;

  const error = getDefaultValueError(control) ?? getDeviceDefaultError(control);
  const isCheckbox = control.control_type === DefaultValueControlType.CHECKBOX;
  const labelId = `${id}-label`;
  const checkboxChoices = isCheckbox ? checkboxChoicesFor(control) : [];
  const ticks = isCheckbox ? resolveTicks(control) : { ticked: [], stale: false };
  const choices = supported ? choicesFor(control) : null;
  const labelClass = compact ? 'text-[10px]' : 'text-xs';
  const fieldClass = compact ? 'h-6 text-[11px]' : 'h-8 text-sm';
  const noteClass = compact ? 'text-[10px]' : 'text-[11px]';
  // Same trimmed match the server applies (exact first, then case-insensitive), so
  // ' yes ' shows as the option 'Yes' it will pre-fill rather than as a stale choice.
  const check = checkDefaultValue(control);
  const selectValue =
    check.kind === 'valid' ? check.value : check.kind === 'none' ? NO_DEFAULT : '';

  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {/* A label bound to the Clear button would override its accessible name. */}
      <Label
        id={labelId}
        htmlFor={supported && !isCheckbox ? id : undefined}
        className={labelClass}
      >
        Default value
      </Label>
      {!supported ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={fieldClass}
          onClick={() => onChange(Array.isArray(control.default_value) ? [] : '')}
        >
          Clear default value
        </Button>
      ) : isCheckbox ? (
        <div role="group" aria-labelledby={labelId} className="space-y-1">
          {checkboxChoices.map((choice, index) => {
            const choiceId = `${id}-choice-${index}`;
            return (
              <div key={choice} className="flex items-center gap-2">
                <Checkbox
                  id={choiceId}
                  checked={ticks.ticked.includes(choice)}
                  onCheckedChange={(checked) =>
                    onChange(toggleTick(checkboxChoices, ticks.ticked, choice, checked === true))
                  }
                />
                <Label htmlFor={choiceId} className={labelClass}>
                  {choice}
                </Label>
              </div>
            );
          })}
          {ticks.stale && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={fieldClass}
              onClick={() => onChange([])}
            >
              Clear default value
            </Button>
          )}
        </div>
      ) : choices !== null ? (
        <Select value={selectValue} onValueChange={(v) => onChange(v === NO_DEFAULT ? '' : v)}>
          <SelectTrigger id={id} className={fieldClass} aria-invalid={error !== null}>
            <SelectValue placeholder={`"${current}" (no longer an option)`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_DEFAULT}>No default</SelectItem>
            {choices.map((choice) => (
              <SelectItem key={choice} value={choice}>
                {choice}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          value={current}
          onChange={(e) => onChange(e.target.value)}
          inputMode={
            control.control_type === DefaultValueControlType.NUMBER ? 'decimal' : undefined
          }
          placeholder="No default"
          aria-invalid={error !== null}
          className={cn(fieldClass, error !== null && 'border-red-500 focus-visible:ring-red-500')}
        />
      )}
      {error !== null && <p className={cn(noteClass, 'text-red-600 dark:text-red-400')}>{error}</p>}
      {autoPopulated && (
        <p className={cn(noteClass, 'text-muted-foreground')}>{AUTO_POPULATE_DEFAULT_HELP}</p>
      )}
    </div>
  );
}
